import { getGeocodeReverse } from '@agos/api-client/geo'
import { useEffect, useRef, useState } from 'react'

import type { LatLng } from './delivery-zones'
import {
  addressFromReverse,
  answerStillCurrent,
  droppedPoint,
  samePoint,
  type PinStatus,
} from './pin-drop'

/**
 * A pin put down on a delivery map, and the one question it asks - FRONTEND.md §9.694 and §9.705.
 *
 * Held inline by the order builder's `DeliveryPinMap` until the customer's checkout needed the same thing (O81);
 * here so the two maps cannot come to disagree about what a drop does. Each screen keeps its own chrome - the
 * till's compact button, the checkout's chip - and hands this the point it holds and what to do with an answer.
 *
 * **One reverse lookup per drop and none otherwise.** The geocode endpoints share 60 requests a minute per address,
 * and the map reports a drop once, on release, never per frame of a drag.
 */
export interface PinDropOptions {
  /** Where the delivery goes, as the screen holds it now. */
  point: LatLng | null
  /** The pin was put down - the screen keeps this point, rounded to six places. */
  onPinMoved: (point: LatLng) => void
  /** A provider named the place under the pin - the screen writes it into its address box. */
  onPinAddress: (address: string) => void
}

export interface PinDrop {
  /** Where the last drop's lookup stands, or `idle` once the point has moved on from it. */
  status: PinStatus
  /** What the map's `onDrop` is wired to. */
  drop: (raw: LatLng) => void
}

export function usePinDrop({ point, onPinMoved, onPinAddress }: PinDropOptions): PinDrop {
  /** The last drop's lookup, and the point it was about - so a point moved on reads as idle again. */
  const [lookup, setLookup] = useState<{ status: PinStatus; about: LatLng | null }>({
    status: 'idle',
    about: null,
  })
  /** The point the screen holds now, read when an answer lands - which is after this render. */
  const held = useRef(point)
  useEffect(() => {
    held.current = point
  }, [point])

  const status: PinStatus = samePoint(lookup.about, point) ? lookup.status : 'idle'

  const ask = async (placed: LatLng) => {
    try {
      const answer = await getGeocodeReverse({ lat: String(placed.lat), lon: String(placed.lng) })
      if (!answerStillCurrent(placed, held.current)) return
      const address = addressFromReverse(answer)
      if (address !== null) onPinAddress(address)
      setLookup({ status: address === null ? 'unnamed' : 'named', about: placed })
    } catch {
      // The point is kept either way: a lookup that failed has not made the delivery impossible.
      if (answerStillCurrent(placed, held.current)) setLookup({ status: 'failed', about: placed })
    }
  }

  const drop = (raw: LatLng) => {
    const placed = droppedPoint(raw)
    held.current = placed
    onPinMoved(placed)
    setLookup({ status: 'asking', about: placed })
    void ask(placed)
  }

  return { status, drop }
}
